import { gmapsLoadPromise } from '../lib/google-maps';
import { BLUE_40, RED_40, WHITE, GREY_50, GREY_40 } from '../lib/colors';

export const DRAWING_DEFAULT: string = BLUE_40;
export const DRAWING_BORDER: string = WHITE;
export const DEACTIVATED_GEOFENCE_FILL_COLOR: string = GREY_50;
export const DEACTIVATED_GEOFENCE_BORDER_COLOR: string = GREY_40;
const DRAWING_ERROR: string = RED_40;

export const GEOFENCE_CLASSIC_THEME_OPACITY: number = 0.4;
export const GEOFENCE_MODERN_THEME_OPACITY: number = 0.2;
export const GEOFENCE_HOVER_OPACITY: number = 0.7;
export const GEOFENCE_STROKE_WEIGHT: number = 1.5;
export const EXISTING_GEOFENCE_OPACITY_MODERN: number = 0.1;
export const EXISTING_GEOFENCE_OPACITY_CLASSIC: number = 0.2;
export const EXISTING_GEOFENCE_STROKE_OPACITY: number = 0.5;

export const GEOFENCE_MARKER_Z_INDEX: number = 5;
export const NEW_GEOFENCE_Z_INDEX: number = 3;
export const MARKER_ICON_Z_INDEX: number = 7;

export enum FLEET_VIEW_MAP_ICON_THEME {
  CLASSIC = 'classic',
  MODERN = 'modern',
}

export type FleetViewMapIconThemes = FLEET_VIEW_MAP_ICON_THEME.CLASSIC | FLEET_VIEW_MAP_ICON_THEME.MODERN;

export interface IGeofenceDrawingMarkerStyles {
  path: google.maps.SymbolPath;
  scale: number;
  fillColor: string;
  fillOpacity: number;
  strokeWeight: number;
  strokeColor: string;
}

export interface IGeofenceDrawingIcons {
  'default': IGeofenceDrawingMarkerStyles;
  'large': IGeofenceDrawingMarkerStyles;
  'error': IGeofenceDrawingMarkerStyles;
  'completed': IGeofenceDrawingMarkerStyles;
}

export interface IDrawingThemeIcons {
  [FLEET_VIEW_MAP_ICON_THEME.MODERN]: IGeofenceDrawingIcons;
  [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: IGeofenceDrawingIcons;
}

export interface ICentroidMarkerIcon {
  icon: string;
}

export interface ICentroidMarkerDrawingIconTypes {
  'default': ICentroidMarkerIcon;
  'error': ICentroidMarkerIcon;
}

export interface ICentroidThemeIcons {
  [FLEET_VIEW_MAP_ICON_THEME.MODERN]: ICentroidMarkerDrawingIconTypes;
  [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: ICentroidMarkerDrawingIconTypes;
}

export interface INewCentroidIconTypes {
  address_added: ICentroidMarkerDrawingIconTypes;
  address_error: ICentroidMarkerDrawingIconTypes;
}

export interface ICentroidIconTypes {
  address_added: ICentroidThemeIcons;
  address_error: ICentroidThemeIcons;
}

export const EXISTING_GEOFENCE_STYLE_OPTIONS = {
  [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
    default: {
      fillColor: BLUE_40,
      strokeColor: BLUE_40,
      fillOpacity: EXISTING_GEOFENCE_OPACITY_MODERN,
      strokeWeight: 1.5,
      strokeOpacity: EXISTING_GEOFENCE_STROKE_OPACITY,
    },
  },
  [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
    default: {
      fillColor: BLUE_40,
      strokeColor: WHITE,
      fillOpacity: EXISTING_GEOFENCE_OPACITY_CLASSIC,
      strokeWeight: 1.5,
      strokeOpacity: EXISTING_GEOFENCE_STROKE_OPACITY,
    },
  },
};

export const GEOFENCE_STYLE_OPTIONS = {
  [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
    default: {
      fillColor: BLUE_40,
      strokeColor: BLUE_40,
      fillOpacity: GEOFENCE_MODERN_THEME_OPACITY,
      strokeWeight: 1.5,
      zIndex: NEW_GEOFENCE_Z_INDEX,
    },
    error: {
      fillColor: RED_40,
      strokeColor: RED_40,
      fillOpacity: GEOFENCE_MODERN_THEME_OPACITY,
      strokeWeight: 1.5,
      zIndex: NEW_GEOFENCE_Z_INDEX,
    },
  },
  [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
    default: {
      fillColor: BLUE_40,
      strokeColor: WHITE,
      fillOpacity: GEOFENCE_CLASSIC_THEME_OPACITY,
      strokeWeight: 1.5,
      zIndex: NEW_GEOFENCE_Z_INDEX,
    },
    error: {
      fillColor: RED_40,
      strokeColor: RED_40,
      fillOpacity: GEOFENCE_CLASSIC_THEME_OPACITY,
      strokeWeight: 1.5,
      zIndex: NEW_GEOFENCE_Z_INDEX,
    },
  },
};

export const POLYLINE_STYLE_OPTIONS = {
  [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
    fillColor: BLUE_40,
    strokeColor: BLUE_40,
    strokeWeight: 2,
    fillOpacity: 1,
    strokeOpacity: 1.0,
  },
  [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
    fillColor: WHITE,
    strokeColor: WHITE,
    strokeWeight: 2,
    fillOpacity: 1,
    strokeOpacity: 1.0,
  },
};

export const centroidMarkerIconsPromise: Promise<any> = gmapsLoadPromise.then((gmaps: any) => {
  const CENTROID_THEME_ICONS: ICentroidIconTypes = {
    address_added: {
      [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
        default: {
          icon: '/map/address-pin-sat.svg',
        },
        error: {
          icon: '/map/address-pin-sat-error.svg',
        },
      },
      [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
        default: {
          icon: '/map/address-pin-street.svg',
        },
        error: {
          icon: '/map/address-pin-street-error.svg',
        },
      },
    },
    address_error: {
      [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
        default: {
          icon: '/map/location-pin-sat.svg',
        },
        error: {
          icon: '/map/location-pin-sat-error.svg',
        },
      },
      [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
        default: {
          icon: '/map/location-pin-street.svg',
        },
        error: {
          icon: '/map/location-pin-street-error.svg',
        },
      },
    },
  };
  return CENTROID_THEME_ICONS;
});

export const newCentroidMarkerIconsPromise: Promise<any> = gmapsLoadPromise.then((gmaps: any) => {
  const CENTROID_THEME_ICONS: INewCentroidIconTypes = {
    address_added: {
      default: {
        icon: '/map/address-pin-street-new.svg',
      },
      error: {
        icon: '/map/address-pin-street-error-new.svg',
      },
    },
    address_error: {
      default: {
        icon: '/map/location-pin-new.svg',
      },
      error: {
        icon: '/map/location-pin-street-error-new.svg',
      },
    },
  };
  return CENTROID_THEME_ICONS;
});

export const markerIconsPromise = gmapsLoadPromise.then((gmaps: any) => {
  const THEME_ICONS: IDrawingThemeIcons = {
    [FLEET_VIEW_MAP_ICON_THEME.CLASSIC]: {
      default: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: WHITE,
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: WHITE,
      },
      large: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: WHITE,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: WHITE,
      },
      error: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 5,
        fillColor: RED_40,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: RED_40,
      },
      completed: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: WHITE,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: WHITE,
      },
    },
    [FLEET_VIEW_MAP_ICON_THEME.MODERN]: {
      default: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: BLUE_40,
        fillOpacity: 1,
        strokeWeight: 1,
        strokeColor: BLUE_40,
      },
      large: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: BLUE_40,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: BLUE_40,
      },
      error: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 5,
        fillColor: RED_40,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: RED_40,
      },
      completed: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 7,
        fillColor: BLUE_40,
        fillOpacity: 1,
        strokeWeight: 0,
        strokeColor: WHITE,
      },
    },
  };
  return THEME_ICONS;
});

export const polylinesPromise = map => gmapsLoadPromise.then((gmaps: any) => {
  return {
    default:  new google.maps.Polyline({
      map,
      zIndex: 4,
    }),

    error: new google.maps.Polyline({
      strokeColor: DRAWING_ERROR,
      strokeWeight: 2,
      map,
      clickable: false,
      zIndex: 6,
    }),

    errorBorder: new google.maps.Polyline({
      strokeWeight: 2,
      strokeColor: DRAWING_ERROR,
      clickable: false,
      zIndex: 5,
      map,
    }),
  };
});

export const polygonPromise = map => gmapsLoadPromise.then((gmaps: any) => {
  return new google.maps.Polygon({
    fillOpacity: GEOFENCE_CLASSIC_THEME_OPACITY,
    clickable: true,
    draggable: true,
    editable: true,
    zIndex: 3,
  });
});

export const circlePromise = () => gmapsLoadPromise.then(() => {
  return new google.maps.Circle({
    fillOpacity: GEOFENCE_CLASSIC_THEME_OPACITY,
    clickable: true,
    draggable: true,
    editable: true,
    zIndex: 3,
  });
});